import type { LabReportView } from '@wodi/shared';
import { Cell, Column, Row, Table, TableBody, TableHeader } from '@wodi/ui';

import { TableScroll } from '../layout/screen.tsx';
import { formatDateOnly } from '../patients/format.ts';
import { flagOf } from './format.ts';
import styles from './lab.module.css';

/**
 * Read-only results (DESIGN.md §7): abnormal values bold with H/L/HH/LL in their own
 * column; critical values in `danger`, the only place the lab screens use it. The flag is
 * never colour alone: the marker has its word beside it.
 */
export function ResultTable({
  report,
  label,
  showPrevious = false,
}: {
  report: Pick<LabReportView, 'results' | 'previous' | 'testName'>;
  label?: string;
  showPrevious?: boolean;
}) {
  return (
    <TableScroll>
      <Table aria-label={label ?? `${report.testName} results`} dense>
        <TableHeader>
          <Column isRowHeader>Test</Column>
          <Column numeric>Result</Column>
          <Column>Flag</Column>
          <Column>Unit</Column>
          <Column>Reference range</Column>
          {showPrevious ? <Column>Previous</Column> : <Column>Comment</Column>}
        </TableHeader>
        <TableBody>
          {report.results.map((r) => {
            const flag = flagOf(r.interpretation);
            const cls = flag.critical
              ? styles.critical
              : flag.emphasised
                ? styles.strong
                : undefined;
            const previous = report.previous.find((p) => p.analyteId === r.analyteId);
            return (
              <Row key={r.id}>
                <Cell>{r.analyteName}</Cell>
                <Cell numeric>
                  <span className={cls}>{r.display}</span>
                </Cell>
                <Cell>
                  <span className={cls}>
                    {flag.marker === '' ? flag.word : `${flag.marker} ${flag.word}`}
                  </span>
                </Cell>
                <Cell>{r.unit?.display ?? ''}</Cell>
                <Cell>
                  {r.range === null
                    ? r.resultType === 'numeric'
                      ? 'No range set'
                      : ''
                    : `${r.range.text}${r.range.provisional ? ' (provisional)' : ''}`}
                </Cell>
                {showPrevious ? (
                  <Cell>
                    {previous === undefined
                      ? 'None'
                      : `${previous.display} on ${formatDateOnly(previous.releasedAt)}`}
                  </Cell>
                ) : (
                  <Cell>{r.comment ?? ''}</Cell>
                )}
              </Row>
            );
          })}
        </TableBody>
      </Table>
    </TableScroll>
  );
}
